import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import UndoIcon from '@mui/icons-material/Undo';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type FormEvent,
  type KeyboardEvent,
  type MouseEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';

import { TruncatedText, useIsTruncated } from '~/components/common/TruncatedText';
import { BUTTON_SX } from '~/theme';
import { visuallyHidden } from '~/utils/visuallyHidden';

import type { GridView } from './gridViewState';

export type GridViewsControlsProps = {
  views: GridView[];
  activeViewId: string | null;
  defaultViewId: string | null;
  hasChanges: boolean;
  /** Set when a write was refused, so a save is never reported that did not happen. */
  saveError: string | null;
  onActivate: (viewId: string | null) => void;
  /** Each of these answers whether the change reached storage. */
  onSaveView: (name: string) => boolean;
  onDiscardChanges: () => void;
  onOverwriteView: (viewId: string) => boolean;
  onRenameView: (viewId: string, name: string) => boolean;
  onDeleteView: (viewId: string) => boolean;
  onSetDefaultView: (viewId: string | null) => boolean;
  onDismissError: () => void;
};

const SYSTEM_VIEW_LABEL = 'System default view';

// The toolbar control is a fixed width so the row never shifts as the label changes; the
// dropdown can afford more. Longer names ellipsis in both and show in full on hover.
const VIEWS_BUTTON_WIDTH = 220;

// One width for every menu this control opens; sized to content they each measured
// differently, so the popup resized under the cursor on the way to saving a view.
const MENU_WIDTH = 420;

// Comfortably inside a menu row at MENU_WIDTH, once the check, star and delete
// controls beside it are accounted for.
const MAX_VIEW_NAME_LENGTH = 40;

const MENU_PAPER_SX = { width: MENU_WIDTH, maxWidth: '100%' };

const ROW_SX = { px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 };

// MUI's Menu closes on Tab, which would leave the per-row controls mouse-only.
const keepTabInsideMenu = (event: KeyboardEvent) => {
  if (event.key === 'Tab') event.stopPropagation();
};

/**
 * Keystrokes in an edit row stay out of the menu's type-ahead, but Escape still has to back
 * out of the row. Without it, Escape pressed anywhere but the text field was swallowed and
 * the row could not be left from the keyboard at all.
 */
const rowKeyDown = (cancel: () => void) => (event: KeyboardEvent) => {
  event.stopPropagation();
  if (event.key === 'Escape') cancel();
};

/**
 * One button for everything about views. Saving and resetting live inside the menu so the
 * toolbar never changes width — as separate buttons they shoved the search box sideways.
 */
export function GridViewsControls({
  views,
  activeViewId,
  defaultViewId,
  hasChanges,
  saveError,
  onActivate,
  onSaveView,
  onDiscardChanges,
  onOverwriteView,
  onRenameView,
  onDeleteView,
  onSetDefaultView,
  onDismissError,
}: GridViewsControlsProps) {
  const nameInputRef = useRef<HTMLInputElement>(null);
  const renameInputRef = useRef<HTMLInputElement>(null);
  const keepButtonRef = useRef<HTMLButtonElement>(null);
  // Focus lands on Keep, which on its own announces only "Keep <name>" — the question it
  // answers has to be tied to the row for a screen reader to read it out.
  const confirmQuestionId = useId();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<string | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openMenu, setOpenMenu] = useState<'views' | 'save' | null>(null);
  const [newName, setNewName] = useState('');

  const { ref: labelRef, isTruncated } = useIsTruncated<HTMLSpanElement>(activeViewId, views);

  const activeView = views.find((view) => view.id === activeViewId) ?? null;
  const selectedId = activeView?.id ?? null;
  const activeLabel = activeView?.name ?? SYSTEM_VIEW_LABEL;
  const resetLabel = activeView ? 'Reset to last saved' : 'Reset table';

  // Two views with one name are indistinguishable in the overwrite list below.
  const nameTaken = (name: string, exceptId?: string) =>
    views.some(
      (view) =>
        view.id !== exceptId && view.name.trim().toLowerCase() === name.trim().toLowerCase(),
    );

  const renameTaken = Boolean(editingId) && nameTaken(editingName, editingId ?? undefined);
  const newNameTaken = nameTaken(newName);

  useEffect(() => {
    if (editingId) renameInputRef.current?.focus();
  }, [editingId]);

  // The button that opened the confirmation has just unmounted, so without this focus falls
  // to the document and the row can be neither confirmed nor escaped from the keyboard.
  // It lands on Keep rather than Delete: this is the destructive step and has no undo.
  useEffect(() => {
    if (confirmingDeleteId) keepButtonRef.current?.focus();
  }, [confirmingDeleteId]);

  const close = () => {
    setOpenMenu(null);
    setNewName('');
    setEditingId(null);
    setConfirmingDeleteId(null);
    onDismissError();
  };

  const startRename = (event: MouseEvent, view: GridView) => {
    event.stopPropagation();
    setConfirmingDeleteId(null);
    setEditingId(view.id);
    setEditingName(view.name);
  };

  const commitRename = (viewId: string) => {
    const name = editingName.trim();
    if (!name || nameTaken(name, viewId)) return;
    if (onRenameView(viewId, name)) setEditingId(null);
  };

  const handleActivate = (viewId: string | null) => {
    onActivate(viewId);
    close();
  };

  const handleSetDefault = (event: MouseEvent, viewId: string | null) => {
    event.stopPropagation();
    onSetDefaultView(defaultViewId === viewId ? null : viewId);
  };

  const startDelete = (event: MouseEvent, viewId: string) => {
    event.stopPropagation();
    setEditingId(null);
    setConfirmingDeleteId(viewId);
  };

  // Closing keeps focus on the button rather than the unmounted row, which left the menu
  // open and deaf to Escape.
  const confirmDelete = (viewId: string) => {
    if (onDeleteView(viewId)) close();
  };

  const handleReset = () => {
    onDiscardChanges();
    close();
  };

  const handleSaveNew = (event: FormEvent) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name || newNameTaken) return;
    if (onSaveView(name)) close();
  };

  const handleOverwrite = (viewId: string) => {
    if (onOverwriteView(viewId)) close();
  };

  const renderDefaultToggle = (viewId: string | null) => {
    const isDefault = defaultViewId === viewId;
    const name = views.find((view) => view.id === viewId)?.name ?? SYSTEM_VIEW_LABEL;
    // Nothing to un-set on the system entry: with no default named, it already opens.
    const fixed = viewId === null && isDefault;

    return (
      <Tooltip title={isDefault ? 'Opens by default' : 'Set as default'}>
        <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
          <IconButton
            size="small"
            disabled={fixed}
            aria-label={
              fixed
                ? `${name} opens by default`
                : isDefault
                  ? `Unset ${name} as default`
                  : `Set ${name} as default`
            }
            onClick={(event) => handleSetDefault(event, viewId)}
          >
            {isDefault ? (
              <StarIcon fontSize="small" color={fixed ? 'disabled' : 'primary'} />
            ) : (
              <StarBorderIcon fontSize="small" />
            )}
          </IconButton>
        </Box>
      </Tooltip>
    );
  };

  const errorBanner = saveError ? (
    <Box sx={{ px: 2, py: 1 }}>
      <Alert severity="error" onClose={onDismissError} sx={{ alignItems: 'center' }}>
        {saveError}
      </Alert>
    </Box>
  ) : null;

  return (
    <>
      {/* describeChild, or the tooltip becomes the button's accessible name instead of the view's. */}
      <Tooltip
        describeChild
        title={hasChanges ? `${activeLabel} has unsaved changes` : isTruncated ? activeLabel : ''}
      >
        <Button
          size="small"
          variant="outlined"
          aria-haspopup="menu"
          aria-expanded={openMenu !== null}
          startIcon={<ViewListIcon />}
          endIcon={<ArrowDropDownIcon />}
          onClick={(event) => {
            setAnchorEl(event.currentTarget);
            setOpenMenu('views');
          }}
          sx={{ ...BUTTON_SX, width: VIEWS_BUTTON_WIDTH, maxWidth: '100%' }}
        >
          <Box
            component="span"
            ref={labelRef}
            sx={{
              flex: 1,
              minWidth: 0,
              textAlign: 'left',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {activeLabel}
          </Box>
          {hasChanges ? (
            <>
              {/* A dot rather than a word: the button is fixed-width and a label crowded the name. */}
              <Box
                component="span"
                aria-hidden
                sx={{
                  flexShrink: 0,
                  ml: 0.75,
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: 'primary.main',
                }}
              />
              {/* The dot is the only visual signal, so its meaning needs a text equivalent. */}
              <Box component="span" sx={{ ...visuallyHidden }}>
                unsaved changes
              </Box>
            </>
          ) : null}
        </Button>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={openMenu === 'views'}
        onClose={close}
        slotProps={{ paper: { sx: MENU_PAPER_SX } }}
      >
        {errorBanner}

        <MenuItem
          selected={selectedId === null}
          onKeyDown={keepTabInsideMenu}
          onClick={() => handleActivate(null)}
        >
          <ListItemIcon>{selectedId === null ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
          <ListItemText disableTypography sx={{ minWidth: 0 }}>
            <TruncatedText>{SYSTEM_VIEW_LABEL}</TruncatedText>
          </ListItemText>
          {renderDefaultToggle(null)}
        </MenuItem>

        {views.map((view) => {
          if (editingId === view.id) {
            // A plain row rather than a MenuItem: typing in a menu item would select it.
            return (
              <Box
                key={view.id}
                component="li"
                onKeyDown={rowKeyDown(() => setEditingId(null))}
                sx={ROW_SX}
              >
                <TextField
                  size="small"
                  sx={{ flex: 1, minWidth: 0 }}
                  label="View name"
                  value={editingName}
                  inputRef={renameInputRef}
                  error={renameTaken}
                  helperText={renameTaken ? 'That name is taken' : undefined}
                  slotProps={{ htmlInput: { maxLength: MAX_VIEW_NAME_LENGTH } }}
                  onChange={(event) => setEditingName(event.target.value)}
                  // Only the field commits: Enter on Cancel used to save the abandoned name.
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') commitRename(view.id);
                  }}
                />
                <Button
                  size="small"
                  variant="contained"
                  disableElevation
                  disabled={!editingName.trim() || renameTaken}
                  onClick={() => commitRename(view.id)}
                  sx={{ ...BUTTON_SX, flexShrink: 0 }}
                >
                  Save
                </Button>
                <Tooltip title="Cancel">
                  <IconButton
                    size="small"
                    aria-label="Cancel rename"
                    onClick={() => setEditingId(null)}
                    sx={{ flexShrink: 0 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          }

          if (confirmingDeleteId === view.id) {
            return (
              <Box
                key={view.id}
                component="li"
                role="group"
                aria-labelledby={confirmQuestionId}
                onKeyDown={rowKeyDown(() => setConfirmingDeleteId(null))}
                sx={ROW_SX}
              >
                <Typography id={confirmQuestionId} variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                  Delete “{view.name}”?
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="contained"
                  disableElevation
                  aria-describedby={confirmQuestionId}
                  onClick={() => confirmDelete(view.id)}
                  sx={{ ...BUTTON_SX, flexShrink: 0 }}
                >
                  Delete
                </Button>
                <Tooltip title="Keep">
                  <IconButton
                    size="small"
                    ref={keepButtonRef}
                    aria-label={`Keep ${view.name}`}
                    aria-describedby={confirmQuestionId}
                    onClick={() => setConfirmingDeleteId(null)}
                    sx={{ flexShrink: 0 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          }

          return (
            <MenuItem
              key={view.id}
              selected={view.id === selectedId}
              onKeyDown={keepTabInsideMenu}
              onClick={() => handleActivate(view.id)}
            >
              <ListItemIcon>
                {view.id === selectedId ? <CheckIcon fontSize="small" /> : null}
              </ListItemIcon>
              <ListItemText disableTypography sx={{ minWidth: 0 }}>
                <TruncatedText>{view.name}</TruncatedText>
              </ListItemText>
              {renderDefaultToggle(view.id)}
              <Tooltip title="Rename view">
                <IconButton
                  size="small"
                  aria-label={`Rename ${view.name}`}
                  onClick={(event) => startRename(event, view)}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete view">
                <IconButton
                  size="small"
                  aria-label={`Delete ${view.name}`}
                  onClick={(event) => startDelete(event, view.id)}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </MenuItem>
          );
        })}

        <Divider />

        {/* Not gated on unsaved changes: naming the table as it stands makes the first view,
            and copying the one you are on needs it too. */}
        <MenuItem onKeyDown={keepTabInsideMenu} onClick={() => setOpenMenu('save')}>
          <ListItemIcon>
            <SaveOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Save view as…</ListItemText>
        </MenuItem>
        <MenuItem disabled={!hasChanges} onKeyDown={keepTabInsideMenu} onClick={handleReset}>
          <ListItemIcon>
            <UndoIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{resetLabel}</ListItemText>
        </MenuItem>

        {views.length === 0 ? (
          <Box sx={{ px: 2, pt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Save the columns, filters, sorting and density you have set up, and come back to them
              from here.
            </Typography>
          </Box>
        ) : null}
      </Menu>

      <Menu
        anchorEl={anchorEl}
        open={openMenu === 'save'}
        onClose={close}
        slotProps={{
          paper: { sx: MENU_PAPER_SX },
          transition: { onEntered: () => nameInputRef.current?.focus() },
        }}
      >
        {errorBanner}

        {/* Keystrokes stay in the field: MenuList's type-ahead otherwise eats them, and a space selects an item. */}
        <Box component="form" onSubmit={handleSaveNew} onKeyDown={rowKeyDown(close)} sx={ROW_SX}>
          <TextField
            size="small"
            sx={{ flex: 1, minWidth: 0 }}
            label="New view name"
            value={newName}
            inputRef={nameInputRef}
            error={newNameTaken}
            helperText={newNameTaken ? 'That name is taken' : undefined}
            slotProps={{ htmlInput: { maxLength: MAX_VIEW_NAME_LENGTH } }}
            onChange={(event) => setNewName(event.target.value)}
          />
          <Button
            type="submit"
            size="small"
            variant="contained"
            disableElevation
            disabled={!newName.trim() || newNameTaken}
            sx={{ ...BUTTON_SX, flexShrink: 0 }}
          >
            Save
          </Button>
          <Tooltip title="Cancel">
            <IconButton
              size="small"
              aria-label="Cancel save view"
              onClick={close}
              sx={{ flexShrink: 0 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {views.length > 0 ? <Divider /> : null}
        {views.length > 0 ? (
          <Box sx={{ px: 2, pt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Or overwrite existing
            </Typography>
          </Box>
        ) : null}
        {views.map((view) => (
          <MenuItem
            key={view.id}
            selected={view.id === selectedId}
            onKeyDown={keepTabInsideMenu}
            onClick={() => handleOverwrite(view.id)}
          >
            <ListItemText disableTypography sx={{ minWidth: 0 }}>
              <TruncatedText>{view.name}</TruncatedText>
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
